/**
 * useOperationalRulesData Hook
 * 운영 규칙 데이터 관리 훅
 */

import { useState, useEffect, useCallback } from 'react';
import { operationalRulesRepository } from '../repositories/OperationalRulesRepository';
import { operationalRulesService } from '../services/OperationalRulesService';
import type {
  OperationalSettings,
  AutoCheckoutRule,
  ReservationRule,
  SeatMovementRule,
  NotificationRule,
  OperatingHoursRule,
  SelfStudyRule,
  MembershipRule,
  ScheduleApprovalRule,
  SimulationResult,
  RulesTabType,
} from '../models/OperationalRules';

export const useOperationalRulesData = () => {
  // State
  const [rules, setRules] = useState<OperationalSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<RulesTabType>('auto-checkout');
  const [simulationResult, setSimulationResult] = useState<SimulationResult | null>(null);
  const [showSimulation, setShowSimulation] = useState(false);

  /**
   * 운영 규칙 로드
   */
  const loadRules = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await operationalRulesRepository.getRules();
      setRules(data);
    } catch (err) {
      console.error('운영 규칙 로드 실패:', err);
      setError('운영 규칙 정보를 불러오는데 실패했습니다.');
      // 실패 시 기본값 사용
      setRules(operationalRulesService.createDefaultSettings());
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 초기 데이터 로드
   */
  useEffect(() => {
    loadRules();
  }, [loadRules]);

  /**
   * 규칙 업데이트 (공통)
   */
  const updateRules = useCallback(async (updatedRules: Partial<OperationalSettings>): Promise<boolean> => {
    try {
      setLoading(true);
      const data = await operationalRulesRepository.updateRules(updatedRules);
      setRules(data);
      return true;
    } catch (err) {
      console.error('규칙 업데이트 실패:', err);
      alert('규칙 업데이트에 실패했습니다.');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 자동 퇴실 규칙 업데이트
   */
  const updateAutoCheckoutRule = useCallback(
    async (autoCheckoutRule: AutoCheckoutRule): Promise<boolean> => {
      // 검증
      const validation = operationalRulesService.validateAutoCheckoutRule(autoCheckoutRule);
      if (!validation.valid) {
        alert(validation.message);
        return false;
      }

      if (!rules) return false;
      const updatedRules = { ...rules, auto_checkout: autoCheckoutRule };
      setRules(updatedRules);

      const success = await updateRules({ auto_checkout: autoCheckoutRule });
      if (success) {
        alert('자동 퇴실 규칙이 업데이트되었습니다.');
      }
      return success;
    },
    [rules, updateRules]
  );

  /**
   * 예약 규칙 업데이트
   */
  const updateReservationRule = useCallback(
    async (reservationRule: ReservationRule): Promise<boolean> => {
      // 검증
      const validation = operationalRulesService.validateReservationRule(reservationRule);
      if (!validation.valid) {
        alert(validation.message);
        return false;
      }

      if (!rules) return false;
      const updatedRules = { ...rules, reservation: reservationRule };
      setRules(updatedRules);

      const success = await updateRules({ reservation: reservationRule });
      if (success) {
        alert('예약 규칙이 업데이트되었습니다.');
      }
      return success;
    },
    [rules, updateRules]
  );

  /**
   * 좌석 이동 규칙 업데이트
   */
  const updateSeatMovementRule = useCallback(
    async (movementRule: SeatMovementRule): Promise<boolean> => {
      // 검증
      const validation = operationalRulesService.validateSeatMovementRule(movementRule);
      if (!validation.valid) {
        alert(validation.message);
        return false;
      }

      if (!rules) return false;
      const updatedRules = { ...rules, seat_movement: movementRule };
      setRules(updatedRules);

      const success = await updateRules({ seat_movement: movementRule });
      if (success) {
        alert('좌석 이동 규칙이 업데이트되었습니다.');
      }
      return success;
    },
    [rules, updateRules]
  );

  /**
   * 알림 규칙 업데이트
   */
  const updateNotificationRule = useCallback(
    async (notificationRule: NotificationRule): Promise<boolean> => {
      // 검증
      const validation = operationalRulesService.validateNotificationRule(notificationRule);
      if (!validation.valid) {
        alert(validation.message);
        return false;
      }

      if (!rules) return false;
      const updatedRules = { ...rules, notifications: notificationRule };
      setRules(updatedRules);

      const success = await updateRules({ notifications: notificationRule });
      if (success) {
        alert('알림 규칙이 업데이트되었습니다.');
      }
      return success;
    },
    [rules, updateRules]
  );

  /**
   * 운영시간 규칙 업데이트
   */
  const updateOperatingHoursRule = useCallback(
    async (operatingHoursRule: OperatingHoursRule): Promise<boolean> => {
      // 검증
      const validation = operationalRulesService.validateOperatingHoursRule(operatingHoursRule);
      if (!validation.valid) {
        alert(validation.message);
        return false;
      }

      if (!rules) return false;
      const updatedRules = { ...rules, operating_hours: operatingHoursRule };
      setRules(updatedRules);

      const success = await updateRules({ operating_hours: operatingHoursRule });
      if (success) {
        alert('운영시간이 업데이트되었습니다.');
      }
      return success;
    },
    [rules, updateRules]
  );

  /**
   * 자율학습 구간 업데이트 (2026-07-28)
   *
   * 시각이 잘못 저장되면 벌점 판정이 통째로 어긋나므로 저장 전에 형식을 검사한다.
   */
  const updateSelfStudyRule = useCallback(
    async (selfStudyRule: SelfStudyRule): Promise<boolean> => {
      const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;
      const fields: Array<[keyof SelfStudyRule, string]> = [
        ['weekday_start', '평일 자율 시작'],
        ['saturday_start', '토요일 자율 시작'],
        ['end_time', '자율 종료'],
      ];
      for (const [field, label] of fields) {
        if (!timePattern.test(String(selfStudyRule[field]))) {
          alert(`${label} 시각을 HH:MM 형식으로 입력해주세요. (예: 22:00)`);
          return false;
        }
      }
      if (selfStudyRule.grace_minutes < 0 || selfStudyRule.grace_minutes > 120) {
        alert('지각 유예 시간은 0~120분 사이로 입력해주세요.');
        return false;
      }

      if (!rules) return false;
      const updatedRules = { ...rules, self_study: selfStudyRule };
      setRules(updatedRules);

      const success = await updateRules({ self_study: selfStudyRule });
      if (success) {
        alert('자율학습 설정이 업데이트되었습니다.');
      }
      return success;
    },
    [rules, updateRules]
  );

  /**
   * 이용권 관리 사용 여부 업데이트 (2026-08-18)
   *
   * 끄면 이용권이 없어도 회원이 '만료'로 바뀌지 않는다. 이미 만료로 찍힌 회원은
   * 회원 목록을 다시 열 때 정상으로 되돌아온다.
   */
  const updateMembershipRule = useCallback(
    async (membershipRule: MembershipRule): Promise<boolean> => {
      if (!rules) return false;
      setRules({ ...rules, membership: membershipRule });

      const success = await updateRules({ membership: membershipRule });
      if (success) {
        alert('이용권 관리 설정이 업데이트되었습니다.');
      }
      return success;
    },
    [rules, updateRules]
  );

  /**
   * 스케줄 변경 운영자 승인 필요 여부 업데이트 (2026-08-19)
   *
   * 끄면 학부모가 저장·동의한 순간 확정된다. 끄더라도 적용은 다음 날부터다.
   */
  const updateScheduleApprovalRule = useCallback(
    async (scheduleApprovalRule: ScheduleApprovalRule): Promise<boolean> => {
      if (!rules) return false;
      setRules({ ...rules, schedule_approval: scheduleApprovalRule });

      const success = await updateRules({ schedule_approval: scheduleApprovalRule });
      if (success) {
        alert('스케줄 승인 설정이 업데이트되었습니다.');
      }
      return success;
    },
    [rules, updateRules]
  );

  /**
   * 자동 퇴실 시뮬레이션 실행
   */
  const runAutoCheckoutSimulation = useCallback(async () => {
    if (!rules) return;

    try {
      const sessionEndTime = new Date();
      sessionEndTime.setHours(sessionEndTime.getHours() + 2);

      const result = await operationalRulesRepository.simulateAutoCheckout(
        sessionEndTime,
        rules.auto_checkout.grace_period_minutes
      );

      setSimulationResult(result);
      setShowSimulation(true);
    } catch (err) {
      console.error('시뮬레이션 실패:', err);
      alert('시뮬레이션 실행에 실패했습니다.');
    }
  }, [rules]);

  /**
   * 예약 시뮬레이션 실행
   */
  const runReservationSimulation = useCallback(async () => {
    if (!rules) return;

    try {
      const reservationTime = new Date();
      reservationTime.setHours(reservationTime.getHours() + 4);

      const result = await operationalRulesRepository.simulateReservation(
        reservationTime,
        rules.reservation.max_advance_hours
      );

      setSimulationResult(result);
      setShowSimulation(true);
    } catch (err) {
      console.error('시뮬레이션 실패:', err);
      alert('시뮬레이션 실행에 실패했습니다.');
    }
  }, [rules]);

  /**
   * 기본값으로 초기화
   */
  const resetToDefaults = useCallback(async () => {
    if (!window.confirm('모든 규칙을 기본값으로 초기화하시겠습니까?')) {
      return false;
    }

    try {
      setLoading(true);
      const data = await operationalRulesRepository.resetToDefaults();
      setRules(data);
      alert('기본값으로 초기화되었습니다.');
      return true;
    } catch (err) {
      console.error('초기화 실패:', err);
      alert('초기화에 실패했습니다.');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // State
    rules,
    loading,
    error,
    activeTab,
    simulationResult,
    showSimulation,

    // Setters
    setActiveTab,
    setShowSimulation,

    // Actions
    loadRules,
    updateAutoCheckoutRule,
    updateReservationRule,
    updateSeatMovementRule,
    updateNotificationRule,
    updateOperatingHoursRule,
    updateSelfStudyRule,
    updateMembershipRule,
    updateScheduleApprovalRule,
    runAutoCheckoutSimulation,
    runReservationSimulation,
    resetToDefaults,
  };
};
